import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Lightning, ShieldCheck, Truck, CreditCard, Wrench, Medal, MapPin, Headset, Recycle, Star,
  Quotes, CaretLeft, CaretRight, BatteryFull, Gauge, Mountains, Buildings, ArrowsIn, Package, Snowflake, WhatsappLogo,
} from "@phosphor-icons/react";
import api, { gbp } from "@/lib/api";
import SEO from "@/components/SEO";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";
import HowToOrder from "@/components/HowToOrder";
import NewsletterBand from "@/components/NewsletterBand";
import StarRating from "@/components/StarRating";
import { BRANDS, BENEFITS, WHY_US, CITIES, SITE } from "@/data/site";
import { useSettings } from "@/context/SettingsContext";

const ICONS = { ShieldCheck, Truck, CreditCard, Wrench, Medal, MapPin, Headset, Recycle, Star, Mountains, Buildings, ArrowsIn, Package, Snowflake, Lightning };

export default function Home() {
  const { settings } = useSettings();
  const hero = settings.hero || {};
  const promo = settings.promo || {};
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [posts, setPosts] = useState([]);
  const [ti, setTi] = useState(0);

  useEffect(() => {
    api.get("/products?featured=true").then((r) => setFeatured(r.data.slice(0, 8)));
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/testimonials").then((r) => setTestimonials(r.data));
    api.get("/blog?limit=6").then((r) => setPosts(r.data.slice(0, 6)));
  }, []);

  const jsonLd = {
    "@context": "https://schema.org", "@type": "LocalBusiness", "@id": "https://ebikesandgear.co.uk/#store",
    name: SITE.name, url: "https://ebikesandgear.co.uk",
    description: "Premium electric bike shop in Kent — 2026 Specialized S-Works & Trek stockist with free UK & EU delivery.",
    telephone: undefined, email: SITE.email, priceRange: "££",
    address: { "@type": "PostalAddress", streetAddress: "St George Lane", addressLocality: "Canterbury", addressRegion: "Kent", postalCode: "CT1 2SY", addressCountry: "GB" },
    openingHoursSpecification: [
      { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "09:00", closes: "18:00" },
      { "@type": "OpeningHoursSpecification", dayOfWeek: "Saturday", opens: "10:00", closes: "17:00" },
    ],
    aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "2184" },
  };

  return (
    <>
      <SEO
        title="Premium Electric Bikes Kent"
        description="Premium electric bikes at St George Lane, Kent CT1 2SY. Shop 2026 Specialized & Trek e-bikes — up to 25% off with free UK & EU delivery. Order today."
        path="/"
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0A0A0A] text-white" data-testid="hero-section">
        <img
          src="/images/site/hero.jpg"
          alt="Riders racing the 2026 Specialized Epic 8 on a mountain trail"
          className="absolute inset-0 h-full w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-black/20" />
        <div className="absolute inset-0 grain opacity-[0.15]" />
        <div className="container-px relative">
          <div className="grid min-h-[88vh] items-center py-20">
            <div className="max-w-2xl">
              <motion.span
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
                className="inline-flex items-center gap-2 rounded-full glass px-4 py-2 text-xs font-bold uppercase tracking-widest"
              >
                <Lightning size={14} weight="fill" className="text-primary" /> {hero.badge || "2026 Elite Collection — In Stock Now"}
              </motion.span>
              <motion.h1
                initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
                className="mt-6 font-heading text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tighter text-balance"
              >
                {hero.headline_1 || "Ride the 2026"}<br /><span className="text-primary">{hero.headline_2 || "Elite Collection."}</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
                className="mt-6 max-w-xl text-lg text-white/75 leading-relaxed"
              >
                {hero.subtext || "Specialized S-Works and Trek flagship bikes, delivered across the UK & EU. Premium carbon e-bikes, road, gravel and the fastest XC bikes of 2026 — with up to 25% off launch pricing."}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
                className="mt-9 flex flex-wrap gap-4"
              >
                <Link to="/shop" className="btn-primary group" data-testid="hero-shop-btn">
                  Shop The Collection <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                </Link>
                <Link to="/category/road" className="rounded-full border border-white/30 px-7 py-3.5 font-semibold transition-all hover:bg-white hover:text-black active:scale-95" data-testid="hero-explore-btn">
                  Explore Road Bikes
                </Link>
              </motion.div>
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.5 }}
                className="mt-12 flex flex-wrap gap-8"
              >
                {[["73", "2026 models"], ["4.9★", "Average rating"], ["12+", "EU countries served"], ["Lifetime", "Frame warranty"]].map(([n, l]) => (
                  <div key={l}>
                    <div className="text-3xl font-black">{n}</div>
                    <div className="text-xs uppercase tracking-wider text-white/50">{l}</div>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* BRANDS MARQUEE */}
      <section className="border-y border-border bg-card py-7" data-testid="brands-marquee">
        <div className="mask-fade-r overflow-hidden">
          <div className="flex w-max animate-marquee gap-16 px-8">
            {[...BRANDS, ...BRANDS].map((b, i) => (
              <span key={i} className="font-heading text-2xl font-black tracking-tight text-muted-foreground/50 whitespace-nowrap">{b}</span>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="container-px py-16 lg:py-24">
        <Stagger className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {BENEFITS.map((b) => {
            const I = ICONS[b.icon];
            return (
              <StaggerItem key={b.title}>
                <div className="group h-full rounded-3xl border border-border bg-card p-6 transition-all hover:-translate-y-1 hover:shadow-soft">
                  <span className="grid h-12 w-12 place-items-center rounded-2xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                    <I size={24} weight="bold" />
                  </span>
                  <h3 className="mt-4 text-lg font-bold">{b.title}</h3>
                  <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{b.text}</p>
                </div>
              </StaggerItem>
            );
          })}
        </Stagger>
      </section>

      {/* CATEGORIES BENTO */}
      <section className="container-px pb-16 lg:pb-24" data-testid="categories-section">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="overline">Find your ride</p>
              <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">Shop by category</h2>
            </div>
            <Link to="/shop" className="link-underline text-sm font-semibold inline-flex items-center gap-1">View all <ArrowRight size={15} /></Link>
          </div>
        </Reveal>
        <div className="mt-10 grid auto-rows-[220px] grid-cols-2 gap-4 lg:grid-cols-4">
          {categories.map((c, i) => (
            <Reveal key={c.slug} delay={i * 0.05} className={i === 0 ? "col-span-2 row-span-2" : i >= 5 ? "col-span-2 lg:col-span-4" : ""}>
              <Link
                to={c.slug === "accessories" ? "/accessories" : `/category/${c.slug}`}
                className="group relative block h-full overflow-hidden rounded-3xl bg-muted"
                data-testid={`category-card-${c.slug}`}
              >
                <img src={c.image} alt={`${c.name} collection`} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-white/70">{c.count} models</p>
                  <h3 className="mt-1 font-heading text-xl font-black text-white">{c.name}</h3>
                  <span className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-white opacity-0 -translate-x-2 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0">
                    Explore <ArrowRight size={15} />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="bg-card py-16 lg:py-24 border-y border-border" data-testid="featured-section">
        <div className="container-px">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="overline">The most sought-after bikes of 2026</p>
                <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">Featured: 2026 Elite Collection</h2>
              </div>
              <Link to="/shop" className="btn-outline !py-2.5 text-sm">View all bikes <ArrowRight size={15} /></Link>
            </div>
          </Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {featured.map((p, i) => (
              <Reveal key={p.id} delay={(i % 4) * 0.05}><ProductCard product={p} index={i} /></Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW TO ORDER */}
      <HowToOrder />

      {/* PROMO BANNER */}
      <section className="container-px py-16 lg:py-24">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2rem] bg-[#0A0A0A] text-white">
            <img src="/images/products/specialized-turbo-levo-gen-4-1.jpg" alt="Specialized Turbo Levo 4 Comp e-MTB" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-40" />
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
            <div className="relative grid gap-6 p-8 sm:p-14 lg:grid-cols-2 lg:items-center">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-1.5 text-xs font-bold uppercase tracking-widest">
                  <Lightning size={14} weight="fill" /> {promo.badge || "Limited Time"}
                </span>
                <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-tight">{promo.title || "Up to 25% off 2026 flagships"}</h2>
                <p className="mt-3 max-w-md text-white/70">{promo.text || "Save 25% on the most sought-after bikes of 2026 — Tarmac SL8 & SL9, Madone SLR 9 and Turbo Levo 4 — plus a fair 15% off all Shimano & SRAM components."}</p>
                <Link to="/shop?sort=price-desc" className="btn-primary mt-7">Shop the sale <ArrowRight size={18} /></Link>
              </div>
              <div className="grid grid-cols-3 gap-4 lg:justify-self-end">
                {[[Gauge, "101Nm", "Levo 4 motor"], [BatteryFull, "6.7kg", "Tarmac SL8"], [ShieldCheck, "Lifetime", "frame warranty"]].map(([I, n, l], idx) => (
                  <div key={idx} className="rounded-2xl glass p-4 text-center">
                    <I size={26} className="mx-auto text-primary" weight="bold" />
                    <div className="mt-2 text-sm font-black">{n}</div>
                    <div className="text-[10px] uppercase tracking-wider text-white/50">{l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* WHY US */}
      <section className="bg-card border-y border-border py-16 lg:py-24" data-testid="why-us-section">
        <div className="container-px">
          <Reveal className="max-w-2xl">
            <p className="overline">Why Ebikes&Gear</p>
            <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">Experience the difference experts make</h2>
          </Reveal>
          <Stagger className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {WHY_US.map((w) => {
              const I = ICONS[w.icon];
              return (
                <StaggerItem key={w.title}>
                  <div className="flex h-full gap-4 rounded-3xl border border-border bg-background p-6">
                    <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-primary/10 text-primary"><I size={24} weight="bold" /></span>
                    <div>
                      <h3 className="text-lg font-bold">{w.title}</h3>
                      <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{w.text}</p>
                    </div>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>
      </section>

      {/* TESTIMONIALS SLIDER */}
      {testimonials.length > 0 && (
        <section className="container-px py-16 lg:py-24" data-testid="reviews-section">
          <Reveal>
            <div className="text-center">
              <p className="overline">Loved across Europe</p>
              <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">What our riders say</h2>
            </div>
          </Reveal>
          <Reveal>
            <div className="relative mx-auto mt-12 max-w-3xl">
              <div className="rounded-[2rem] border border-border bg-card p-8 sm:p-12 text-center shadow-soft">
                <Quotes size={48} weight="fill" className="mx-auto text-primary/30" />
                <p className="mt-6 text-xl sm:text-2xl font-medium leading-relaxed text-balance">{testimonials[ti].text}</p>
                <div className="mt-6 flex flex-col items-center gap-1">
                  <StarRating value={testimonials[ti].rating} size={18} />
                  <p className="mt-2 font-bold">{testimonials[ti].name}</p>
                  <p className="text-sm text-muted-foreground">{testimonials[ti].role} · {testimonials[ti].location}</p>
                </div>
              </div>
              <div className="mt-6 flex items-center justify-center gap-3">
                <button onClick={() => setTi((ti - 1 + testimonials.length) % testimonials.length)} className="grid h-11 w-11 place-items-center rounded-full border border-border hover:bg-muted" aria-label="Previous review" data-testid="review-prev"><CaretLeft size={18} /></button>
                <div className="flex gap-2">
                  {testimonials.map((_, i) => (
                    <button key={i} onClick={() => setTi(i)} className={`h-2 rounded-full transition-all ${i === ti ? "w-7 bg-primary" : "w-2 bg-border"}`} aria-label={`Review ${i + 1}`} />
                  ))}
                </div>
                <button onClick={() => setTi((ti + 1) % testimonials.length)} className="grid h-11 w-11 place-items-center rounded-full border border-border hover:bg-muted" aria-label="Next review" data-testid="review-next"><CaretRight size={18} /></button>
              </div>
            </div>
          </Reveal>
        </section>
      )}

      {/* LATEST ARTICLES */}
      {posts.length > 0 && (
        <section className="bg-card border-y border-border py-16 lg:py-24" data-testid="articles-section">
          <div className="container-px">
            <Reveal>
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                  <p className="overline">From the journal</p>
                  <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">Latest articles & guides</h2>
                </div>
                <Link to="/blog" className="link-underline text-sm font-semibold inline-flex items-center gap-1">All articles <ArrowRight size={15} /></Link>
              </div>
            </Reveal>
            <div className="mt-10 grid gap-6 md:grid-cols-3">
              {posts.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.07}>
                  <Link to={`/blog/${p.slug}`} className="group block overflow-hidden rounded-3xl border border-border bg-background transition-all hover:-translate-y-1 hover:shadow-soft" data-testid={`article-card-${p.slug}`}>
                    <div className="aspect-[16/10] overflow-hidden">
                      <img src={p.cover} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-2">
                        <span className="inline-block rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-primary">{p.category}</span>
                        <span className="text-xs text-muted-foreground">{p.read_time}</span>
                      </div>
                      <h3 className="mt-2 text-lg font-bold leading-tight group-hover:text-primary transition-colors">{p.title}</h3>
                      <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{p.excerpt}</p>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* NEWSLETTER */}
      <section className="container-px pt-16 lg:pt-20">
        <Reveal><NewsletterBand /></Reveal>
      </section>

      {/* LOCAL SEO CITIES */}
      <section className="container-px py-16 lg:py-24">
        <Reveal className="text-center">
          <p className="overline">Serving riders everywhere</p>
          <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">Electric bikes across Europe</h2>
          <p className="mt-3 mx-auto max-w-2xl text-muted-foreground">From the UK to mainland Europe, we deliver premium ebikes and expert support to riders in every major city.</p>
        </Reveal>
        <div className="mx-auto mt-8 flex max-w-4xl flex-wrap justify-center gap-3">
          {CITIES.map((city) => (
            <Link key={city} to="/shop" className="rounded-full border border-border bg-card px-5 py-2.5 text-sm font-semibold transition-all hover:border-primary hover:text-primary">
              Electric Bikes {city}
            </Link>
          ))}
        </div>
      </section>

      {/* SHOWROOM MAP */}
      <section className="bg-card border-t border-border py-16 lg:py-24" data-testid="showroom-section">
        <div className="container-px grid gap-10 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <p className="overline">Visit us</p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">Come test ride at our Kent showroom</h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">Book a free test ride and let our experts help you find your perfect electric bike. No pressure, just genuine advice.</p>
            <ul className="mt-6 space-y-3 text-sm">
              <li className="flex items-center gap-3"><MapPin size={20} className="text-primary" /> {SITE.address}</li>
              <li className="flex items-center gap-3"><WhatsappLogo size={20} className="text-primary" /> <a href={SITE.whatsappHref} target="_blank" rel="noreferrer" className="hover:text-primary" data-testid="showroom-whatsapp-link">WhatsApp {SITE.phone}</a></li>
              <li className="flex items-center gap-3"><Headset size={20} className="text-primary" /> <a href={`mailto:${SITE.email}`} className="hover:text-primary" data-testid="showroom-email-link">{SITE.email}</a></li>
            </ul>
            <Link to="/contact" className="btn-primary mt-7">Get directions <ArrowRight size={18} /></Link>
          </Reveal>
          <Reveal>
            <div className="overflow-hidden rounded-3xl border border-border shadow-soft">
              <iframe
                title="Ebikes&Gear showroom map"
                src={SITE.mapsEmbed}
                className="h-[360px] w-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
